import { useCallback, useEffect, useState } from "react";
import { createDefaultAnalyticsDateRange } from "../dashboard/Drafts";

async function readApiJson(response) {
  const rawText = await response.text();
  if (!rawText) {
    return {};
  }

  try {
    return JSON.parse(rawText);
  } catch {
    const compact = rawText.replace(/\s+/g, " ").trim();
    const preview = compact.slice(0, 180) || "Response body is not valid JSON";
    throw new Error(preview);
  }
}

export default function ProductAnalyticsAdmin({
  adminFetch,
  apiBaseUrl,
  canManageProducts,
  adminProducts,
  locale,
  setAdminError,
  setAdminToast
}) {
  const [productAnalyticsById, setProductAnalyticsById] = useState({});
  const [productAnalyticsDailyById, setProductAnalyticsDailyById] = useState({});
  const [productAnalyticsLoading, setProductAnalyticsLoading] = useState(false);
  const [productAnalyticsExporting, setProductAnalyticsExporting] = useState(false);
  const [productAnalyticsRange, setProductAnalyticsRange] = useState(() => createDefaultAnalyticsDateRange());

  const clearProductAnalytics = useCallback(() => {
    setProductAnalyticsById({});
    setProductAnalyticsDailyById({});
  }, []);

  const updateProductAnalyticsRange = useCallback((field, value) => {
    if (!field || !["startDate", "endDate"].includes(field)) {
      return;
    }
    const normalized = String(value || "").trim();
    setProductAnalyticsRange((prev) => ({
      ...prev,
      [field]: normalized
    }));
  }, []);

  const fetchProductsAnalyticsOverview = useCallback(async (productsInput = adminProducts) => {
    if (!canManageProducts) {
      clearProductAnalytics();
      return;
    }

    const productList = Array.isArray(productsInput) ? productsInput : [];
    const productIds = Array.from(
      new Set(
        productList
          .map((item) => Number(item?.id || 0))
          .filter((id) => Number.isFinite(id) && id > 0)
      )
    );

    if (!productIds.length) {
      clearProductAnalytics();
      return;
    }

    setProductAnalyticsLoading(true);
    try {
      const query = new URLSearchParams({
        productIds: productIds.join(","),
        startDate: String(productAnalyticsRange.startDate || ""),
        endDate: String(productAnalyticsRange.endDate || ""),
        ts: String(Date.now())
      });

      const [overviewResponse, dailyResponse] = await Promise.all([
        adminFetch(`${apiBaseUrl}/admin/products/analytics/overview?${query.toString()}`),
        adminFetch(`${apiBaseUrl}/admin/products/analytics/daily?${query.toString()}`)
      ]);
      const [overviewData, dailyData] = await Promise.all([
        readApiJson(overviewResponse),
        readApiJson(dailyResponse)
      ]);

      if (!overviewResponse.ok) {
        throw new Error(overviewData.message || "Product analytics load failed");
      }
      if (!dailyResponse.ok) {
        throw new Error(dailyData.message || "Product analytics trend load failed");
      }

      const rows = Array.isArray(overviewData?.overview?.rows) ? overviewData.overview.rows : [];
      const nextMap = rows.reduce((acc, item) => {
        const productId = Number(item?.productId || 0);
        if (!Number.isFinite(productId) || productId <= 0) {
          return acc;
        }
        acc[String(productId)] = item;
        return acc;
      }, {});

      const dailyProducts = Array.isArray(dailyData?.summary?.products) ? dailyData.summary.products : [];
      const nextDailyMap = dailyProducts.reduce((acc, item) => {
        const productId = Number(item?.productId || 0);
        if (!Number.isFinite(productId) || productId <= 0) {
          return acc;
        }
        acc[String(productId)] = Array.isArray(item?.daily)
          ? item.daily.map((dayItem) => ({
            date: String(dayItem?.date || "").trim(),
            viewCount: Number(dayItem?.viewCount || 0)
          }))
          : [];
        return acc;
      }, {});

      setProductAnalyticsById(nextMap);
      setProductAnalyticsDailyById(nextDailyMap);
      setAdminError("");
    } catch (err) {
      if (err.message !== "Session expired" && err.message !== "Forbidden") {
        setAdminError(err.message || "Product analytics load failed");
      }
    } finally {
      setProductAnalyticsLoading(false);
    }
  }, [
    adminFetch,
    adminProducts,
    apiBaseUrl,
    canManageProducts,
    clearProductAnalytics,
    productAnalyticsRange.endDate,
    productAnalyticsRange.startDate,
    setAdminError
  ]);

  const exportProductsAnalyticsDailyCsv = useCallback(async () => {
    const productIds = Array.from(
      new Set(
        adminProducts
          .map((item) => Number(item?.id || 0))
          .filter((id) => Number.isFinite(id) && id > 0)
      )
    );

    if (!productIds.length) {
      return;
    }

    setProductAnalyticsExporting(true);
    try {
      const query = new URLSearchParams({
        productIds: productIds.join(","),
        startDate: String(productAnalyticsRange.startDate || ""),
        endDate: String(productAnalyticsRange.endDate || ""),
        exportCsv: "1",
        ts: String(Date.now())
      });

      const response = await adminFetch(`${apiBaseUrl}/admin/products/analytics/daily?${query.toString()}`);
      if (!response.ok) {
        const data = await readApiJson(response);
        throw new Error(data.message || "Product analytics export failed");
      }

      const csvText = await response.text();
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
      const objectUrl = window.URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = `product-analytics-daily-${productAnalyticsRange.startDate}-to-${productAnalyticsRange.endDate}.csv`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.URL.revokeObjectURL(objectUrl);
      setAdminToast(locale === "zh" ? "日报已导出" : "Daily report exported");
    } catch (err) {
      if (err.message !== "Session expired" && err.message !== "Forbidden") {
        setAdminError(err.message || "Product analytics export failed");
      }
    } finally {
      setProductAnalyticsExporting(false);
    }
  }, [
    adminFetch,
    adminProducts,
    apiBaseUrl,
    locale,
    productAnalyticsRange.endDate,
    productAnalyticsRange.startDate,
    setAdminError,
    setAdminToast
  ]);

  useEffect(() => {
    if (!Array.isArray(adminProducts) || !adminProducts.length) {
      clearProductAnalytics();
      return;
    }

    fetchProductsAnalyticsOverview(adminProducts);
  }, [adminProducts, clearProductAnalytics, fetchProductsAnalyticsOverview]);

  return {
    productAnalyticsById,
    productAnalyticsDailyById,
    productAnalyticsLoading,
    productAnalyticsExporting,
    productAnalyticsRange,
    updateProductAnalyticsRange,
    fetchProductsAnalyticsOverview,
    exportProductsAnalyticsDailyCsv
  };
}
